import React, { useState, useEffect } from 'react';
import Styles, { CenterLoader, Loader, useScreen } from '../../styles';
import { get, set, size, first, isObject, sortBy, uniq } from 'global/lib/lodash';
import clsx from 'clsx';
import moment from 'moment';
import { Typography, FormControlLabel, Switch } from '@mui/material';
import { ColorSlider } from '../color-slider';
import useDebounce from '../../hooks/useDebounce';
import useRetrieveData from '../../hooks/useRetrieveData';
import useStyles from './styles';
import { getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from 'feature/admin/account-settings';
import { HeatChart } from './HeatChart';
import { avgItems, sumCount } from './utils';
import { DataChart } from './Chart';
import { Column, Row } from '../row';
import { IconButton } from '../Button';
import { getHeatchartScore } from '../../utils/getDisplayScore';
import { cleanCopy, ensureArray, exportToCsv, mergeDeepIgnoreNulls, uppercaseFirst } from '../../utils';
import { Text } from '../text';
import Modal, { FullScreen } from '../modal';
import Filters from '../../../feature/report/Filters';
import colors from '../../styles/colors';
import Form from '../../components/form';
import { useUser } from '../../hooks/useUser';
import { useSelector } from 'react-redux';
import { buildFilters } from 'global/utils/filter';
import { userHasPermissions } from 'redux/actions/userLogin';
import { FilterChips } from '../filterchips';
import { DateRanges } from '../../lib/filter';
import { getRowDefs } from '../../../feature/report/ReportData/DataReports';
import { unset } from '../../lib/lodash';
import { testNativeSelect } from '../print/print-view';

export const cleanChartFilters = (filter) => {
  const filters = buildFilters(filter, {
    dontFilter: ['ownership', 'quality_type', 'user_id'],
    dontSort: true,
    overrideFilter: (f) => {
      if (f.realms) {
        f.realm_id = uniq([f.realm_id, ...f.realms.split(',')].filter((f) => !!f)).join(',');
      }

      return {
        ...f,
        _showCompare: filter._showCompare,
        realms: undefined,
      };
    },
  });

  if (get(filters, 'filter.scorecard_id') === 0) {
    set(filters, 'filter.scorecard_id', undefined);
  }

  if (get(filters, 'filter.superset_id') === 0) {
    set(filters, 'filter.superset_id', undefined);
  }

  if (!['all', 'none'].includes(get(filter, 'daterange'))) {
    const fdr = DateRanges.find((dr) => dr.value == filter.daterange);
    set(filters, 'filter.start_date', get(fdr, 'vals.0'));
    set(filters, 'filter.end_date', get(fdr, 'vals.1'));
  }

  set(filters, 'filter._daterange', filter.daterange);

  return filters;
};

export const Charting = (props) => {
  const { isMobileXS } = useScreen();
  const classes = useStyles({ isMobile: isMobileXS });
  const styles = Styles();
  const { rows, columns, chips, chartSettings } = props;
  const [processing, setProcessing] = useState(false);
  const ranges = getAccountSetting(SETTINGS.assessmentColorScale);
  const [slide, setSlide] = useState(props.slide || ranges || [0, 100]);
  const slideDebounce = useDebounce(slide, 500);

  const isSavedChart = !!props.originalChartData;

  const [modal, setModal] = useState({
    open: false,
    chartIndex: null,
  });
  const [editChart, setEditChart] = useState({
    open: false,
    onClose: () => {
      setEditChart((s) => ({ ...s, open: false }));
    },
  });

  const chartGlobalFilters = useSelector((s) => get(s, 'filter.reports.data'));

  const [state, setState] = useState({
    showCounts: props.showCounts !== undefined ? props.showCounts : false,
    useChart: props.useChart !== undefined ? props.useChart : false,
    data: [],
    compare: [],
    compareType: 'diff',
    loading: props.loading,
    chartFilters: null,
    chipFilters: null,
    rows: null,
  });

  const headerRow = state.headerRow || props.headerRow;
  const compareHeader = state.compareHeader || props.compareHeader;

  const isDate = ['yearly', 'quarterly', 'monthly'].indexOf(columns) > -1;

  const [chart, setChart] = useState({
    title: '',
    cols: [],
    tables: [], //array of array of rows
    header: [],
    includeRowAverage: false,
    includeColAverage: false,
    error: undefined,
  });

  const data = state.data;
  const loading = state.loading;
  const isCompare = !!state.compare;
  const is2colcomp = isCompare && state.compareType == 'twocol';

  const hasData = !!size(data) && !!rows && !!columns && !loading && (props.chartIndex !== undefined ? !!get(data, props.chartIndex) : true);

  const getSelectedColumnGroupByColumn = () => {
    let gb = get(props, 'selectedColumn.groupBy');
    if (typeof gb === 'function') {
      return gb(props);
    }
    return gb;
  };

  const setupFilters = () => {
    setState((s) => {
      const ocd = get(props.originalChartData, 'config.params.filter', {});
      if (ocd.realm_type) {
        ocd.realm_type = ensureArray(ocd.realm_type);
      }
      const chartFilters = mergeDeepIgnoreNulls(ocd, chartGlobalFilters, {
        daterange: !['all', 'none'].includes(chartGlobalFilters.daterange) ? chartGlobalFilters.daterange : get(props.originalChartData, 'config.daterange'),
      });

      set(chartFilters, 'daterange', chartGlobalFilters.daterange);

      return {
        ...s,
        chartFilters,
      };
    });
  };

  const getSelectedRow = () => {
    if (props.getSelectedRow) {
      return props.getSelectedRow('rows', { selected: { rows: state.rows || props.rows } });
    }
    return props.selectedRow;
  };

  useEffect(() => {
    if (props.loading) {
      setChart((c) => ({ ...c, error: undefined }));
      setState((s) => ({ ...s, loading: true }));
    } else {
      setState((s) => ({ ...s, ...props, data: props.data, compare: props.compare, compareType: props.compareType, loading: false }));
    }
  }, [props.loading, props.data]);

  const getCompareTitle = (ind = 0) => {
    return get(state, `compareTitles.${ind}.title`, '');
  }

  useEffect(() => {
    if (hasData && !loading) {
      if (data.error) {
        return setChart({ ...chart, error: data.error });
      }

      let colArr = [];
      const tableMap = {};
      const includeColAverage = get(props, 'selectedColumn.showColAvg', false) || get(getSelectedRow(), 'showColAvg', false);

      let colKey = isDate ? columns.replace('ly', '') : columns;

      const getCol = (d = {}, setRow = false) => {
        const { month, year } = d;
        const groupBy = getSelectedColumnGroupByColumn() || colKey;

        if (isDate && year) {
          const dateKey = columns.replace('ly', '');
          colKey = `relationships.${dateKey}`;
          if (setRow) {
            let _month = parseInt(month) - 1; //months start at 0 in moment
            _month = isNaN(_month) ? 1 : _month;
            const tme = dateKey === 'quarter' ? moment().month(_month).year(year).startOf('month').toISOString() : moment().month(_month).year(year).startOf(dateKey).toISOString();
            set(d, `relationships.${dateKey}.id`, tme);
          }
        } else {
          colKey = `relationships.${groupBy}`;
        }

        return get(d, colKey);
      };

      const getRow = (d) => {
        const groupBy = get(getSelectedRow(), 'groupBy', state.rows || rows);
        return get(d, `relationships.${groupBy}`);
      };

      const getTableId = (d) => {
        let tableBy = get(getSelectedRow(), 'tableBy');
        if (typeof tableBy === 'function') {
          tableBy = tableBy(props);
        }
        return tableBy ? get(d, `relationships.${tableBy}.id`, 0) : 0;
      };

      const getTableRow = (d) => {
        let tableBy = get(getSelectedRow(), 'tableBy');
        if (typeof tableBy === 'function') {
          tableBy = tableBy(props);
        }
        return get(d, `relationships.${tableBy}`);
      };

      const getComp = d => {
        if (!isCompare) { return null; }

        const col = getCol(d);
        const row = getRow(d);

        const rowid = get(row, 'id', 0);
        const colid = get(col, 'id', col);

        const fo = state.compare.find(c => {
          const ccol = getCol(c);
          const crow = getRow(c);

          let edid = true;
          if (d.edition_id && c.edition_id) {
            edid = d.edition_id == c.edition_id;
          }

          const crowid = get(crow, 'id', 0);
          const ccolid = get(ccol, 'id', ccol);

          return crowid == rowid && ccolid == colid && edid;
        });

        return fo || 'none';
      }

      data.map((d) => {
        const row = getRow(d);
        const col = getCol(d, true);

        const rowid = get(row, 'id', 0);
        const colid = get(col, 'id', col);
        const tableid = getTableId(d);
        
        d.comp = getComp(d);

        if (colid !== undefined && !colArr.find((c) => get(c, 'id') === colid)) {
          const name = get(col, 'name', colid || 'none');

          if (is2colcomp) {
            colArr.push({ ...col, name: getCompareTitle(0), cat: name, colKey });
            colArr.push({ ...col, isComp: true, name: getCompareTitle(1), cat: name, colKey });
          } else {
            colArr.push({ ...col, name, colKey });  
          }
        }

        if (is2colcomp) {
          set(tableMap, [tableid, rowid], [...get(tableMap, [tableid, rowid], []), d, d.comp]);
        } else {
          set(tableMap, [tableid, rowid], [...get(tableMap, [tableid, rowid], []), d]);
        }
      });

      colArr = colArr.sort((a, b) => {
        const isDate = moment(a.id).isValid();

        if (isDate) {
          return moment(a.id) - moment(b.id);
        } else {
          return a.id - b.id;
        }
      });

      const cols = {}; //map of col averages
      const tablerows = {};

      const sortRows = (_rows) => {
        const hasOrder = _rows.find((r) => r.find((rr) => rr.order !== undefined));
        if (hasOrder) {
          return sortBy(_rows, (o) =>
            get(
              (o || []).find((oo) => oo.order !== undefined),
              'order'
            )
          );
        } else {
          return sortBy(_rows, (o) => get(o, '0.value'));
        }
      };

      Object.keys(tableMap).map((k, _i) => {
        const arr = get(tableMap, k, []).filter((a) => a);

        const tabrows = arr.map((ar) => {
          const group = getRow(ar[0]);
          const title = get(group, 'name');
          const tooltip = get(group, 'description');

          const rowcalcs = colArr.map((c, i) => {
            i = includeColAverage ? i + (is2colcomp ? 2 : 1) : i;
            const hasVal = ar.find((a) => {
              const hasId = get(getCol(a), 'id', getCol(a)) === get(c, 'id');
              if (c.isComp) { 
                return hasId && !a.comp;
              }
              return hasId;
            });

            const standardcalc = hasVal ? parseFloat(get(hasVal, 'calculated_value', 0)) : 'N/A';
            let calc = standardcalc;
            if (isCompare) {
              if (!is2colcomp) {
                const v = parseFloat(get(hasVal, 'calculated_value', 0));
                const cv = parseFloat(get(hasVal, 'comp.calculated_value', 0));
                calc = hasVal && hasVal.comp !== 'none' ? v - cv : 'N/A';
              }
            }

            const count = get(hasVal, 'count');
            //get our column averages
            const configKey = 'relationships.edition.config';

            const config = get(hasVal, configKey, get(ar.find((a) => get(a, configKey)), configKey));

            if (isCompare && !is2colcomp) {
              set(config, 'is_simple_score', true);
            }

            const ob = {
              value: calc,
              count,
              isComp: !!c.isComp,
              id: get(c, 'id'),
              order: get(hasVal, 'order'),
              config,
              relationships: get(hasVal, 'relationships'),
            };
            set(cols, [_i, i], [...get(cols, [_i, i], []), ob]);
            return ob;
          });

          const titleClick = (() => {
            const { tier } = getSelectedRow() || {};
            const edition = get(ar, '0.relationships.edition');
            if (tier === 'scorecard' || tier === 'superset') {
              return () => {
                let obj = { [`${state.rows || rows}_id`]: { id: get(group, 'id'), name: title } };

                if (!!edition) {
                  obj = {
                    ...obj,
                    ...(isSavedChart ? state.chipFilters : {}),
                    edition_id: { id: get(edition, 'id'), name: get(edition, 'name') },
                  };
                }

                if (isSavedChart) {
                  const srows = get(getRowDefs(getSelectedRow().id), 'rowSelect.rows', state.rows);
                  setState((s) => ({ ...s, chipFilters: obj, rows: srows }));
                } else {
                  setModal((m) => ({ ...m, chartIndex: 0 }));
                  props.onRowSelect && props.onRowSelect(obj);
                }
              };
            }
          })();

          const rowAvg = {
            rowcalcs,
            value: avgItems(rowcalcs),
            count: sumCount(rowcalcs),
            config: get(
              rowcalcs.find((r) => !!r.config),
              'config'
            ),
          };

          if (is2colcomp) {
            const odd = rowcalcs.filter((f, i) => !((i % 2) == 0));
            const rowAvgOdd = {
              rowcalcs,
              isComp: !!odd.find(o => o.isComp),
              value: avgItems(odd),
              count: sumCount(odd),
              config: get(
                odd.find((r) => !!r.config),
                'config'
              ),
            };

            const even = rowcalcs.filter((f, i) => (i % 2) == 0);
            const rowAvgEven = {
              rowcalcs,
              isComp: !!even.find(o => o.isComp),
              value: avgItems(even),
              count: sumCount(even),
              config: get(
                even.find((r) => !!r.config),
                'config'
              ),
            };
            set(cols, [_i, 0], [...get(cols, [_i, 0], []), rowAvgEven]);
            set(cols, [_i, 1], [...get(cols, [_i, 1], []), rowAvgOdd]);

            return [{ value: title, tooltip, onClick: titleClick }, ...[rowAvgEven, rowAvgOdd], ...rowcalcs];
          } else {
            set(cols, [_i, 0], [...get(cols, [_i, 0], []), rowAvg]);
            return [{ value: title, tooltip, onClick: titleClick }, ...[rowAvg], ...rowcalcs];
          }
        });
        const rr = getTableRow(first(first(arr)));
        set(tablerows, _i, {
          title: get(rr, 'name'),
          rows: sortRows(tabrows),
        });
      });
      
      const cchart = {
        ...chart,
        colKey,
        cols,
        header: [...(is2colcomp ? [{ name: getCompareTitle(0), cat: 'Average' }, { name: getCompareTitle(1), cat: 'Average' }] : ['Average']), ...colArr],
        tables: Object.keys(tablerows).map((k) => tablerows[k]),
        includeColAverage,
        isCompare,
        is2colcomp,
      }

      setChart(cchart);
    }
  }, [data, rows, columns, loading, slideDebounce, headerRow]);

  useEffect(() => {
    setupFilters();
  }, [chartGlobalFilters, modal.open]);

  const loadChartFilters = async (f = state.chartFilters, opts = {}) => {
    try {
      setState((s) => ({ ...s, chartFilters: f, loading: true }));
      const ochartcopy = cleanCopy(opts.originalChartData || props.originalChartData);

      const cfilters = state.chipFilters ? props.getChipFilters(state.chipFilters) : {};
      const orow = state.rows || get(ochartcopy, 'config.params.meta.rows');
      // const newRow = state.chipFilters ? get(getRowDefs(get(getSelectedRow(), 'id')), 'rowSelect.rows', orow) : orow;
      const filters = cleanChartFilters({ ...f, ...cfilters });

      const res = await chartSettings.loadChartData(ochartcopy, filters.filter, orow);
      setState((s) => ({ ...s, ...res }));
    } catch (err) {
      alert(err);
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => {
    if (!!state.chipFilters) {
      loadChartFilters();
    }
  }, [state.chipFilters]);

  const exportTo = (index, opts = { chart }) => {
    try {
      testNativeSelect().then(() => {
        setProcessing(true);

        const expRows = [['Title', ...opts.chart.header.map((h) => getHeaderTitle({ val: h, ...state }).val) ]];

        const tabs = typeof index === 'number' ? [opts.chart.tables[index]] : opts.chart.tables;

        tabs.map((tabl, i) => {
          expRows.push([tabl.title]);

          if (opts.chart.includeColAverage) {
            expRows.push([
              headerAvgTitle(tabl, state.showCounts),
              ...get(opts.chart, `cols.${index}`, []).map((val, _i) => {
                const ck = colKey(tabl);
                const __i = chart.is2colcomp && _i == 1 ? 0 : _i;
                const foundHeaderRow = findHeaderRow(ck, val);
                const cols = cleanCopy(get(chart, `cols.${index}`));

                if (chart.is2colcomp && _i == 1) {
                  set(cols, '0.0.isComp', true);
                }

                const headerRowItems = __i === 0 ? findHeaderRow(ck, cols, true) || [] : [];

                const value = ck
                  ? (__i === 0
                    ? avgItems(headerRowItems.map((hr) => ({ value: parseFloat(get(hr, 'calculated_value', 0)) })))
                    : parseFloat(get(foundHeaderRow, 'calculated_value')))
                  : avgItems(val);

                const count = ck ? (__i === 0 ? headerRowItems.reduce((a, c) => a + get(c, 'count', 0), 0) : get(foundHeaderRow, 'count', 0)) : sumCount(val);

                return getDisplayVal({ ...val, val: { value, config: get(val, '0.config'), count } }).display;
              }),
            ]);
          }

          get(tabl, `rows`, [])
            .map((col) => col.map((c) => getDisplayVal({ ...c, val: c }).display))
            .map((r) => expRows.push(r));
        });

        const fileName = (() => {
          return `DataExport-${new Date().toISOString()}.csv`;
        })();

        exportToCsv(fileName, expRows);
      }).finally(() => {
        setProcessing(false);
      });
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const changeCounts = () => {
    setState((s) => ({ ...s, showCounts: !s.showCounts }));
  };

  const changeChart = () => {
    setState((s) => ({ ...s, useChart: !s.useChart }));
  };

  const hasRange = slide[0] !== 0 || slide[1] !== 100;

  const getDisplayVal = (row) => {
    let { val } = row;
    const { value, config, tooltip, count } = row.val;

    let isVal = !!isObject(val);
    const hasClick = get(row, 'val.onClick');
    let isLink = false;
    let scoreVal = val;
    let display = val;
    let hcScore;

    if (typeof value === 'string' || typeof value === 'undefined') {
      if (hasClick) {
        isLink = true;
      }
      isVal = false;
      if (get(getSelectedRow(), 'id') === 'checkpoint' && tooltip) {
        display = `${value}: ${tooltip}`;
      } else {
        display = value || 'None';
      }
    } else {
      if (value === -1) {
        isVal = true;
      }
      scoreVal = value;
      hcScore = getHeatchartScore(row.val, config, state.useChart ? slide : undefined);
      display = hcScore;
    }

    if (state.showCounts && count !== undefined) {
      display = count;
    }

    if (isCompare && !is2colcomp && parseFloat(display) > 0 && !state.showCounts) {
      display = `+${display}`;
    }

    const disableClick = !modal.open && !!props.isSingleChart;

    return {
      isVal,
      isLink,
      hasClick,
      scoreVal,
      display,
      slide,
      hcScore,
      ...(disableClick ? { isLink: false, hasClick: false } : {}),
    };
  };

  const colKey = (tab) => {
    const gb = get(props, 'selectedColumn.groupBy');
    if (!!size(headerRow) && chart.includeColAverage && gb) {
      let ck = get(props, 'selectedColumn.colKey', gb);
      if (typeof ck === 'function') {
        ck = ck({ ...props, selectedRow: getSelectedRow() });
      }
      const useRelId = size(chart.tables) > 1 ? true : false;
      return { useRelId, id: `${ck}_id` };
    }
    return null;
  };

  const headerAvgTitle = (tab, showCounts = false) => {
    if (size(chart.tables) > 1 || isSavedChart) {
      return get(tab, 'title');
    }
    let selRowId = get(getSelectedRow(), 'parent');
    if (selRowId === 'scorecard_header') {
      selRowId = 'scorecard';
    }

    let keyy = get(chips, `${selRowId}_id.name`, selRowId || (showCounts ? 'Total' : 'Average'));
    return uppercaseFirst(keyy);
  };

  const getHeaderTitle = (row) => {
    let { val, showCounts } = row;
    const id = get(row, 'val.id');
    const colKey = get(row, 'val.colKey', '');
    const isString = typeof id === 'string';

    let header = get(val, 'cat');

    if (id && isString && moment(id).isValid()) {
      const format = colKey.endsWith('year') ? 'YYYY' : colKey.endsWith('quarter') ? 'MMM YYYY' : 'L';
      
      if (is2colcomp) {
        header = get(val, 'isComp') ? moment(id).format(format) : header;
      } else {
        val = moment(id).format(format);
      }
    }
    if (showCounts && ([val, get(val, 'name')].includes('Average'))) {
      return { val: 'Total' };
    }
    return { val, header: header || val };
  };

  const findHeaderRel = (ck, val, rows, returnAll = false) => {
    if (!ck || !val || !Array.isArray(rows)) {
      return;
    }

    const { id, useRelId } = ck;
    const [key, _id] = id.split('_');
    const valId = returnAll
      ? val.reduce((a, c) => a || c.reduce((ac, cc) => ac || get(cc, useRelId ? `relationships.${key}.id` : 'id'), null), null)
      : val.reduce((a, c) => a || get(c, useRelId ? `relationships.${key}.id` : 'id'), null);
    const filtered = useRelId ? rows.filter((hr) => get(hr, id) == valId) : rows;
    if (returnAll) {
      return filtered;
    }

    return filtered.find((hr) => get(hr, `${get(props, 'selectedColumn.groupBy', 'realm')}${isDate ? '' : '_id'}`) == get(val, '0.id'));
  }

  const findHeaderRow = (ck, val, returnAll = false) => {
    const comps = {
      headerRow,
      compareHeader,
    }

    const hasComp = (val || []).find(r => get(r, 'isComp')) || get(val, '0.0.isComp');
    const headRow = comps[!!hasComp ? 'compareHeader' : 'headerRow'];

    let returnRow = findHeaderRel(ck, val, headRow, returnAll);
    if (isCompare && !is2colcomp) {
      const compRow = findHeaderRel(ck, val, comps.compareHeader, returnAll);

      const findCompRow = rr => {
        const keys = [];

        Object.keys(rr).map(k => {
          if (k.includes('_id')) { keys.push(k) }
        });

        return compRow.find(c => size(keys.filter(k => get(c, k) == get(rr, k))) == size(keys));
      }

      if (returnAll) {
        returnRow = findHeaderRel(ck, val, comps.headerRow, returnAll);

        const newVals = returnRow.map((rr, i) => {
          const crow = findCompRow(rr);
          if (!crow) { return { ...rr, calculated_value: null } }
          return { 
            ...rr, 
            _display_value: parseFloat(get(rr, 'display_value', 0)) - parseFloat(get(crow, `display_value`, 0)) ,
            calculated_value: parseFloat(get(rr, 'calculated_value', 0)) - parseFloat(get(crow, `calculated_value`, 0)) 
          } 
        });

        const returnAvg = avgItems(returnRow.map(r => ({ value: parseFloat(r.calculated_value) })));
        const compAvg = avgItems(compRow.map(r => ({ value: parseFloat(r.calculated_value) })))

        const avgDiff = returnAvg - compAvg;
        set(newVals, '0.avgDiff', avgDiff);

        return newVals;
      }
      return returnRow && compRow ? { ...returnRow, calculated_value: get(returnRow, 'calculated_value', 0) - get(compRow, 'calculated_value', 0) } : null;
    }
    
    return returnRow;
  };

  const renderBar = (table, index, opts = {}) => {
    opts = {
      includeTitle: false,
      ...opts,
    };
    return (
      <Row style={{ padding: 8 }}>
        {opts.includeTitle && <Text>{table.title}</Text>}
        <Row style={{ marginLeft: 'auto' }}>
          <IconButton onClick={() => exportTo(index)} title="Download Chart Data" loading={processing} icon="save_alt" size="small" variant="none" />
          <IconButton onClick={() => modalChart(table, index)} style={{ transform: 'rotate(45deg)' }} icon="unfold_more" size="small" variant="none" />
        </Row>
      </Row>
    );
  };

  const chartProps = {
    ...props,
    ...state,
    getDisplayVal,
    findHeaderRow,
    headerAvgTitle,
    getHeaderTitle,
    colKey,
    renderBar,
    is2colcomp,

    hasRange,
    slide,
    slideDebounce,
    isDate,
    hasData,
    chart,
    chartGlobalFilters,
  };

  const saveChart = (table, chartIndex) => {
    setEditChart((s) => ({
      ...s,
      open: true,
      title: 'SAVE CHART',
      chart: isSavedChart
        ? props.originalChartData
        : {
            name: table.title,
          },
      onSubmit: async (vals, p) => {
        await chartSettings.createAndSave(
          isSavedChart
            ? { chart: { ...props.originalChartData, ...vals } }
            : {
                ...vals,
                chartProps: p.chartProps,
                chartIndex,
              }
        );
        s.onClose();
        props.setAlerts &&
          props.setAlerts({
            message: () => (
              <span>
                Chart saved! To view/edit saved charts, visit the <a href="/account/data">Account/Data</a> page
              </span>
            ),
          });
      },
    }));
  };

  const modalChart = (table, index) => {
    setModal((s) => ({
      ...s,
      open: true,
      title: table.title || props.name,
      onClose: () => {
        setModal((s) => ({ ...s, open: false }));
        setState((s) => ({ ...s, chipFilters: null, rows: null }));
        setTimeout(() => {
          if (props.loadCharts) { 
            props.loadCharts() 
          } else {
            loadChartFilters(undefined, isSavedChart ? {} : { originalChartData: { config: { params: get(props, 'params', {}) } } });
          }
        }, 200);
      },
      table,
      chartIndex: index,
    }));
  };

  const renderSliderBar = (opts = {}) => {
    const showSlider = get(props, 'showSlider', true);

    if (!showSlider && !modal.open) {
      return null;
    }

    opts = {
      asRow: false,
      ...opts,
    };
    return (
      <div style={{ display: 'flex', flexDirection: opts.asRow && !isMobileXS ? 'row' : 'column' }}>
        <div className={clsx(classes.slideContainer, classes.hidePrint)}>
          <Typography variant={isMobileXS ? 'caption' : 'subtitle1'} color={isMobileXS ? 'textSecondary' : undefined}>
            Color Scale
          </Typography>

          <div className={classes.slideWrap}>
            <ColorSlider value={slide} onChange={(e, v) => setSlide(v)} />
          </div>
        </div>
        <Row style={{ flexDirection: isMobileXS ? 'column' : 'row' }}>
          <FormControlLabel
            className={clsx(classes.switch, classes.hidePrint)}
            control={<Switch checked={!!state.showCounts} onChange={changeCounts} />}
            label={state.showCounts ? 'Hide observation counts' : 'Show observation counts'}
          />
          <FormControlLabel className={clsx(classes.switch, classes.hidePrint)} control={<Switch checked={!!state.useChart} onChange={changeChart} />} label={'Use Chart'} />
        </Row>
      </div>
    );
  };

  const ChartRender = (p) => {
    if (!loading && !hasData) {
      return <div className={classes.loadBuffer}>{'No results, try updating the filters to include more data.'}</div>;
    }
    return state.useChart ? <DataChart {...p} /> : <HeatChart {...p} />;
  };

  const renderChart = () => {
    return ChartRender(chartProps);
  };

  const renderModalChart = () => {
    const filterProps = {
      filter: state.chartFilters,
      onChange: async (f) => {
        loadChartFilters(f, isSavedChart ? {} : { originalChartData: { config: { params: get(props, 'params', {}) } } });
      },
    };

    const chipArray = !!state.chipFilters
      ? Object.keys(state.chipFilters)
          .filter((k) => !!state.chipFilters[k])
          .map((k) => ({ ...state.chipFilters[k], key: k }))
      : null;

    return (
      <div style={{ width: '100%' }}>
        <Row>
          <Filters
            display="data"
            asRow={!isMobileXS}
            vendors={props.vendors}
            onlyFilters={[
              'startDate',
              'endDate',
              'status',
              props.columns !== 'company' ? 'vendors' : null,
              ...(['builder', 'division', 'community', 'lot'].includes(props.rows) ? ['edition', 'scorecard'] : []),
            ].filter((f) => !!f)}
            {...filterProps}
          />
        </Row>
        {renderSliderBar({ asRow: true })}
        {!!chipArray && <FilterChips filters={chipArray} onDelete={(f) => setState((s) => ({ ...s, chipFilters: { ...s.chipFilters, [f.key]: undefined } }))} />}
        {loading ? <CenterLoader /> : ChartRender({ ...chartProps, chartIndex: modal.chartIndex })}
      </div>
    );
  };

  // console.log({
  //   chartProps,
  //   chart
  // })

  if (chart.error) {
    return <div className={clsx(classes.loadBuffer)}>{data.error}</div>;
  }

  return (
    <>
      {hasData && !loading && renderSliderBar()}
      <div className={clsx(isSavedChart && classes.container)}>
        {!!props.name && (
          <Column center>
            <Text type="title_medium" bold className={classes.tinyMarginTop}>
              {props.name}
            </Text>
            <Text type="title_small" className={classes.smallMarginTop}>
              {props.description}
            </Text>
            <FilterChips includeRealm={false} filter={state.chartFilters} id="reports.data" canDelete={false} dontShow={['ownership']} />
            <div style={{ position: 'absolute', top: 0, right: 0 }}>{renderBar(data, props.chartIndex)}</div>
          </Column>
        )}

        {loading && (
          <div className={clsx(styles.centerColumn, classes.loadBuffer)}>
            <Loader size={30} />
          </div>
        )}

        {!loading && renderChart()}
      </div>

      <FullScreen 
        {...chartProps} 
        {...modal} 
        renderEndIcon={(p) => !modal.open ? null : (
          <Row style={{ marginLeft: 'auto', color: colors.white }}>
            {userHasPermissions(['manageAccount']) && <IconButton style={{ color: colors.white }} icon="save" onClick={() => saveChart(modal.table, modal.chartIndex)} variant="none" size="large" />}
            <IconButton style={{ color: colors.white }} icon="save_alt" onClick={() => exportTo(modal.chartIndex, p)} variant="none" size="large" />
          </Row>
        )}
      >
        {modal.open && renderModalChart()}
      </FullScreen>

      <EditChartModal {...editChart} chartProps={chartProps} />
    </>
  );
};

export const EditChartModal = (props) => {
  const usergroups = useRetrieveData({ url: `/user-groups` }, { build: ['userGroups'], useCache: { reload: 60 } });
  const { me } = useUser();

  return (
    <Modal {...props}>
      {!!props.open && (
        <Form
          showAllScreens
          onSubmit={(v) => props.onSubmit(v, props)}
          submitText="Submit"
          canSubmit={true}
          submitBtnBoxStyle={{ position: 'relative' }}
          useCheckContinue
          screens={[
            {
              title: '',
              inputs: [
                {
                  id: 'name',
                  title: 'Title',
                  type: 'TextField',
                  defaultValue: () => get(props, 'chart.name', ''),
                  props: {
                    label: 'Title',
                    required: true,
                  },
                },
                {
                  id: 'description',
                  title: 'Description',
                  type: 'TextField',
                  defaultValue: () => get(props, 'chart.description', ''),
                  props: {
                    label: 'Description',
                  },
                },
                {
                  id: 'userGroups',
                  title: 'User Group',
                  type: 'Select',
                  defaultValue: () => get(props, 'chart.userGroups', get(me, 'userGroups', [])).map((u) => `${u.id || u}`),
                  data: () => usergroups.data,
                  item: (d) => ({ title: d.name, value: d.id }),
                  props: {
                    label: 'User Group',
                    multiple: true,
                    required: true,
                  },
                },
                {
                  id: 'realms',
                  type: 'DropRealms',
                  defaultValue: () => (get(props, 'chart.realms', []).find((r) => r.tier === 'division') ? get(props, 'chart.realms', []) : get(props, 'chart.realms', [])),
                  props: {
                    label: 'Access',
                    placeholder: 'Global',
                    useNewHist: true,
                    onlyTiers: ['builder', 'division'],
                    helperText: 'Leave this blank to to make the chart available to all users',
                  },
                },
              ],
            },
          ]}
        />
      )}
    </Modal>
  );
};
